import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Loader2, Users, Swords, CalendarDays } from "lucide-react";
import api, { reportApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FORMATS = [
  { id: "bracket", label: "Knockout bracket", hint: "Head-to-head rounds. Reps join, you start it when the bracket is full." },
  { id: "score", label: "Open scoreboard", hint: "Everyone plays the same customer; best scorecard by the deadline wins." },
];

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>;
}

export function AdminTournaments({ personas }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", persona_id: "", days: 7, format: "bracket", max_players: 8 });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const load = () => api.get("/tournaments").then((r) => setList(r.data)).catch(reportApiError("AdminTournaments"));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!form.persona_id && personas?.length) set("persona_id", personas[0].id); }, [personas]); // eslint-disable-line

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give the tournament a name");
    setBusy(true);
    try {
      await api.post("/admin/tournaments", { ...form, days: Number(form.days), max_players: Number(form.max_players) });
      toast.success("Tournament created", { description: "It's live on the Tournaments page for every rep." });
      setForm((f) => ({ ...f, name: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create the tournament"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,420px),1fr] gap-4" data-testid="admin-tournaments">
      <div className="card-surface rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /><h3 className="font-bold text-slate-900">Create a weekly tournament</h3></div>
        <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Friday Knockout — Week 23" data-testid="tournament-name-input" /></Field>
        <Field label="Customer everyone faces">
          <select value={form.persona_id} onChange={(e) => set("persona_id", e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="tournament-persona-select">
            {(personas || []).map((p) => <option key={p.id} value={p.id}>{p.name} — {p.title}</option>)}
          </select>
        </Field>
        <Field label="Format">
          <div className="grid gap-2">
            {FORMATS.map((f) => (
              <button key={f.id} type="button" onClick={() => set("format", f.id)} className={`text-left rounded-xl border p-3 transition-colors ${form.format === f.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`tournament-format-${f.id}`}>
                <div className="text-sm font-semibold text-slate-900">{f.label}</div><div className="text-xs text-slate-500">{f.hint}</div>
              </button>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Runs for (days)"><Input type="number" min={1} max={30} value={form.days} onChange={(e) => set("days", e.target.value)} data-testid="tournament-days-input" /></Field>
          {form.format === "bracket" && (
            <Field label="Players">
              <select value={form.max_players} onChange={(e) => set("max_players", Number(e.target.value))} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="tournament-size-select">
                {[4, 8, 16].map((n) => <option key={n} value={n}>{n} players</option>)}
              </select>
            </Field>
          )}
        </div>
        <Button onClick={create} disabled={busy} className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="create-tournament-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trophy className="w-4 h-4 mr-2" /> Create tournament</>}
        </Button>
      </div>
      <div className="space-y-2">
        <h3 className="font-bold text-slate-900 text-sm">All tournaments ({list.length})</h3>
        {list.length === 0 && <div className="card-surface rounded-2xl p-6 text-sm text-slate-500">None yet — create the first one on the left.</div>}
        {list.map((t) => (
          <div key={t.id} className="card-surface rounded-2xl p-4 flex items-center gap-3" data-testid={`admin-tournament-${t.id}`}>
            <img src={t.persona_avatar} alt="" className="w-10 h-10 rounded-full object-cover object-top" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{t.name}</div>
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                <span className="inline-flex items-center gap-1"><Swords className="w-3 h-3" /> {t.persona_title}</span>
                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {t.entries}{t.format === "bracket" ? `/${t.max_players}` : ""} joined</span>
                <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> ends {new Date(t.ends_at).toLocaleDateString("en-GB")}</span>
              </div>
            </div>
            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border ${t.status === "completed" ? "bg-slate-100 text-slate-600 border-slate-200" : t.status === "open" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{t.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
